import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';
import { PAGE_TITLE } from '../../constants/common';
import { OWNER_ROLE, ROLE_ACCESS, ROLES } from '../../constants/roles';

// Enums
import { Action } from '../../enums/action';
import { CognitoUserStatus } from '../../enums/cognito-user-status';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';
import { DynamicObjects } from '../../components/Table/types/ArrayOfDynamicObjects.type';
import { ElementSize } from '../../types/element-size';

// Components
import Table from '../../components/Table/Table';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import Modal from '../../components/Modal/Modal';
import ResetPasswordModal from '../../components/ResetPasswordModal/ResetPasswordModal';
import Tooltip from '../../components/Tooltip';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconSwitcher from '../../components/Icons/IconSwitcher/IconSwitcher';
import IconEnvelope from '../../components/Icons/IconEnvelope/IconEnvelope';
import IconBin from '../../components/Icons/IconBin/IconBin';
import IconLock from '../../components/Icons/Lock/Lock';
import { AbilityContext } from '../../components/Can/Can';

interface MembersViewOwnProps {
  title: string;
}

interface Member {
  id: string;
  user: {
    email: string;
    first_name: string;
    last_name: string;
    cognito_status: CognitoUserStatus;
  };
  role: string;
  active: boolean;
}

const ACTION_NAMES = {
  REINVITE: 'REINVITE',
  EDIT: 'EDIT',
  ACTIVATE: 'ACTIVATE',
  DELETE: 'DELETE',
  RESET_PASSWORD: 'RESET_PASSWORD',
} as const;

const MembersView: React.FC<ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & MembersViewOwnProps> = ({
  title,
  getMembers,
  members,
  updateMember,
  reInviteMember,
  deleteMember,
  resetMemberPassword,
  profile,
}) => {
  const ability = useContext(AbilityContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isMembersLoaded, setIsMembersLoaded] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [memberToResetPassword, setMemberToResetPassword] = useState<Member | null>(null);

  const fetchMembers = useCallback(async () => {
    await getMembers();
    setIsMembersLoaded(true);
  }, [getMembers]);

  const membersView = useMemo(() => {
    return members.map((member) => ({
      Email: member.user.email,
      'First Name': member.user.first_name,
      'Last Name': member.user.last_name,
      Role: member.role,
      Active: member.active,
      id: member.id,
      cognitoStatus: member.user.cognito_status,
    }));
  }, [members]);

  const handleClickNewUser = () => {
    navigate(ROUTE.CREATE_MEMBERS);
  };

  const handleDeleteMember = async (id: string) => {
    await deleteMember(id);
    await fetchMembers();
  };

  const closeModalDeletion = useCallback(() => setMemberToDelete(null), []);

  const openModalDeletion = useCallback((member: Member) => setMemberToDelete(member), []);

  const acceptDeleteMember = () => {
    if (!memberToDelete) return;
    handleDeleteMember(memberToDelete.id);
    closeModalDeletion();
  };

  const closeModalResetPassword = useCallback(() => setMemberToResetPassword(null), []);

  const openModalResetPassword = useCallback((member: Member) => setMemberToResetPassword(member), []);

  const acceptResetPassword = async (password: string) => {
    if (!memberToResetPassword) return;
    await resetMemberPassword({ id: memberToResetPassword.id, password });
  };

  const actionsButtons = useMemo<IActionsDropdown[]>(() => {
    const base: IActionsDropdown[] = [
      {
        icon: () => (
          <Tooltip hint={t('commonText.reInvite')}>
            <div className="flex items-center justify-center transition-opacity duration-300">
              <IconEnvelope size={ElementSize.XXS} />
            </div>
          </Tooltip>
        ),
        onClick: async (id: string) => {
          await reInviteMember({ id });
        },
        loading: false,
        isShow: (item) => item.cognitoStatus === CognitoUserStatus.FORCE_CHANGE_PASSWORD,
        name: ACTION_NAMES.REINVITE,
      },
      {
        icon: () => (
          <Tooltip hint={t('commonText.edit')}>
            <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
          </Tooltip>
        ),
        onClick: (id: string) => {
          const path = generatePath(ROUTE.EDIT_MEMBER, { id });
          navigate(path);
        },
        loading: false,
        tag: IActionsTag.Link,
        name: ACTION_NAMES.EDIT,
      },
      {
        icon: (item) => (
          <Tooltip hint={t('commonText.activate')}>
            <div
              className={`flex items-center justify-center ${item.Active ? 'text-success' : 'text-danger'}`}
            >
              <IconSwitcher size={ElementSize.XXS} />
            </div>
          </Tooltip>
        ),
        onClick: async (id: string, payload) => {
          await updateMember({
            id,
            formData: {
              active: !payload.Active,
              role: payload.Role,
            },
          });
          await fetchMembers();
        },
        loading: false,
        name: ACTION_NAMES.ACTIVATE,
      },
      {
        icon: () => (
          <Tooltip hint={t('commonText.resetPassword')}>
            <IconLock size={ElementSize.XXS} />
          </Tooltip>
        ),
        onClick: (_, member) => openModalResetPassword(member as unknown as Member),
        loading: false,
        name: ACTION_NAMES.RESET_PASSWORD,
      },
    ];

    if (ability.can(Action.Delete, PAGE_TITLE.MEMBERS)) {
      base.push({
        icon: () => (
          <Tooltip hint={t('commonText.delete')}>
            <IconBin fill="#000" size={ElementSize.XXS} />
          </Tooltip>
        ),
        onClick: (_, member) => openModalDeletion(member as unknown as Member),
        loading: false,
        name: ACTION_NAMES.DELETE,
      });
    }

    return base;
  }, [ability]);

  const actionFilter = useCallback(
    (value: DynamicObjects, actions: IActionsDropdown[]) => {
      const isCurrentUser = profile?.id && value.id === profile?.id;
      let filteredActions = isCurrentUser
        ? actions.filter((a) => a.name !== ACTION_NAMES.ACTIVATE && a.name !== ACTION_NAMES.RESET_PASSWORD)
        : actions;

      const isSupervisor = profile?.role === ROLES.SUPERVISOR;
      const isProtectedRole = value.Role === OWNER_ROLE || value.Role === ROLES.SUPERVISOR;

      if (isCurrentUser || (isSupervisor && isProtectedRole)) {
        filteredActions = filteredActions.filter((a) => a.name !== ACTION_NAMES.DELETE);
      }

      // Supervisors can't edit an Owner's record at all (backend CASL blocks it).
      if (!isCurrentUser && isSupervisor && value.Role === OWNER_ROLE) {
        filteredActions = filteredActions.filter((a) => a.name !== ACTION_NAMES.EDIT);
      }

      const isHigherLevelThanTarget = ROLE_ACCESS[profile?.role ?? ''] > ROLE_ACCESS[(value.Role as string) ?? ''];

      if (!isCurrentUser && !isHigherLevelThanTarget) {
        filteredActions = filteredActions.filter(
          (a) => a.name !== ACTION_NAMES.RESET_PASSWORD && a.name !== ACTION_NAMES.ACTIVATE
        );
      }

      // Operators cannot activate/deactivate any member, regardless of hierarchy.
      if (profile?.role === ROLES.OPERATOR) {
        filteredActions = filteredActions.filter((a) => a.name !== ACTION_NAMES.ACTIVATE);
      }

      return filteredActions;
    },
    [profile]
  );

  useEffect(() => {
    fetchMembers();
  }, []);


  if (!isMembersLoaded) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="p-members-view flex h-full w-full flex-col">
      <div className="mb-5 flex w-full items-center justify-between">
        <h2 className="text-title-md2 font-semibold text-black dark:text-white">{title}</h2>
        <InteractiveButton text={t('member.common.newMember')} onClick={handleClickNewUser} />
      </div>

      <Table
        values={membersView}
        defaultKeys={['Email', 'First Name', 'Last Name', 'Role', 'Active']}
        actionsButtons={actionsButtons}
        actionBtnFilter={actionFilter}
      />

      <Modal
        modalTitle={t('member.modals.delete.title')}
        isVisible={Boolean(memberToDelete)}
        content={t('member.modals.delete.confirmDelete', '', { member: memberToDelete ? `${memberToDelete['First Name']} ${memberToDelete['Last Name']}` : t('member.common.member') })}
        acceptBtnText={t('commonText.delete')}
        onClose={closeModalDeletion}
        onAccept={acceptDeleteMember}
        showAcceptBtn
        showCancelBtn
      />

      <ResetPasswordModal
        isVisible={Boolean(memberToResetPassword)}
        memberName={memberToResetPassword ? `${memberToResetPassword['First Name']} ${memberToResetPassword['Last Name']}` : t('member.common.member')}
        onClose={closeModalResetPassword}
        onSubmit={acceptResetPassword}
      />
    </div>
  );
};

const mapState = (state: RootState) => ({
  members: state.members.members,
  profile: state.members.currentUserMember,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getMembers: dispatch.members.getMembers,
  updateMember: dispatch.members.updateMember,
  reInviteMember: dispatch.members.reInviteMember,
  deleteMember: dispatch.members.deleteMember,
  resetMemberPassword: dispatch.members.resetMemberPassword,
});

export default connect(mapState, mapDispatch)(MembersView);
